import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { InternalFormSubmission, InternalFormSubmissionStatus } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getInternalFormSubmissions } from '../../../../redux/services/internalFormsService';
import { DillColumn, DillTable } from '../../../general/Dill/DillTable';
import { DillButton } from '../../../general/Dill';
import { openDillToast } from '../../../../utils/helpers';
import SendInternalFormModal from './SendInternalFormModal';
import { getFormTypeLabel } from './internalFormUtils';

type InternalFormSubmissionRow = Omit<InternalFormSubmission, 'accessToken'>;

const STATUS_STYLES: Record<InternalFormSubmissionStatus, { label: string; className: string }> = {
  SENT: { label: 'Sent', className: 'bg-gray-100 text-gray-700 border-gray-200' },
  AWAITING_SIGNATURES: {
    label: 'Awaiting Signatures',
    className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  SUBMITTED: {
    label: 'Ready for Decision',
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  APPROVED: { label: 'Approved', className: 'bg-green-50 text-green-700 border-green-200' },
  REJECTED: { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200' },
};

export const InternalFormStatusBadge = ({ status }: { status: InternalFormSubmissionStatus }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${STATUS_STYLES[status].className}`}>
    {STATUS_STYLES[status].label}
  </span>
);

const formatDate = (date?: Date | string | null) =>
  date ? moment(date).format('MM/DD/YYYY hh:mm A') : '-';

const InternalFormSubmissions = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [submissions, setSubmissions] = useState<InternalFormSubmissionRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    if (!currentSupplier?.id) return;
    setIsLoading(true);
    const result = await dispatch(getInternalFormSubmissions({ supplierId: currentSupplier.id }));
    setIsLoading(false);
    if (result.type.includes('fulfilled')) {
      setSubmissions((result as any).payload?.data ?? []);
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result as any).payload || 'Failed to load internal forms',
      });
    }
  }, [currentSupplier?.id, dispatch]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const columns: DillColumn<InternalFormSubmissionRow>[] = useMemo(
    () => [
      {
        key: 'templateName',
        label: 'Form',
        width: 220,
        sortable: true,
        render: (_, row) => (
          <div className="flex flex-col">
            <p className="text-sm font-medium">{row.templateName}</p>
            <p className="text-[11px] GREY_500-CLR">{getFormTypeLabel(row)}</p>
          </div>
        ),
      },
      {
        key: 'billToCustomerId',
        label: 'Bill-To Customer #',
        width: 160,
        sortable: true,
        render: (_, row) => <p className="text-sm">{row.billToCustomerId || '-'}</p>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 160,
        sortable: true,
        render: (_, row) => <InternalFormStatusBadge status={row.status} />,
      },
      {
        key: 'sentByName',
        label: 'Sent By',
        width: 180,
        sortable: true,
        render: (_, row) => <p className="text-sm">{row.sentByName || row.sentByEmail}</p>,
      },
      {
        key: 'recipientEmails',
        label: 'Sent To',
        width: 240,
        render: (_, row) => (
          <p className="text-sm truncate" title={row.recipientEmails.join(', ')}>
            {row.recipientName
              ? `${row.recipientName} (${row.recipientEmails.join(', ')})`
              : row.recipientEmails.join(', ')}
          </p>
        ),
      },
      {
        key: 'createdAt',
        label: 'Sent',
        width: 170,
        sortable: true,
        getSortValue: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
        render: (_, row) => <p className="text-sm">{formatDate(row.createdAt)}</p>,
      },
      {
        key: 'submittedAt',
        label: 'Submitted',
        width: 170,
        sortable: true,
        getSortValue: (row) => (row.submittedAt ? new Date(row.submittedAt).getTime() : 0),
        render: (_, row) => <p className="text-sm">{formatDate(row.submittedAt)}</p>,
      },
    ],
    []
  );

  return (
    <div className="h-full p-4 flex">
      <SendInternalFormModal
        open={isSendModalOpen}
        handleClose={() => setIsSendModalOpen(false)}
        onSent={fetchSubmissions}
      />
      <div className="w-full h-full">
        <DillTable
          columns={columns}
          data={submissions}
          rowKey={(row) => row.id}
          sortable
          defaultSort={{ key: 'createdAt', dir: 'desc' }}
          pagination
          pageSize={20}
          search={false}
          striped={false}
          loading={isLoading}
          emptyMessage="No internal forms have been sent yet."
          onRowClick={(row) => navigate(row.id)}
          toolbarRight={
            <DillButton label="Send Internal Form" onClick={() => setIsSendModalOpen(true)} />
          }
          rowHeight={56}
          countLabel="Internal Forms"
        />
      </div>
    </div>
  );
};

export default InternalFormSubmissions;
